import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  Loader2, 
  DollarSign, 
  Users, 
  Briefcase,
  TrendingUp,
  Crown,
  CreditCard,
  RefreshCw,
  ShieldAlert,
  LogOut
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PayoutDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [txFilter, setTxFilter] = useState('all');
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, txRes] = await Promise.all([
        axios.get(`${API}/admin/payout-stats`),
        axios.get(`${API}/admin/transactions?limit=50`)
      ]);
      setStats(statsRes.data);
      setTransactions(txRes.data);
      setAccessDenied(false);
    } catch (error) {
      console.error('Failed to fetch payout data:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
        toast.error('Admin access required');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredTransactions = transactions.filter(tx => {
    if (txFilter === 'all') return true;
    return tx.type === txFilter;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center" data-testid="access-denied">
        <div className="industrial-card p-12 text-center max-w-md">
          <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-white uppercase mb-2">Access Denied</h1>
          <p className="text-stone-400 mb-6">You don't have permission to view this page. Admin access is required.</p>
          <Link to="/">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">
              Return Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950" data-testid="payout-dashboard">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
                alt="IronLink" 
                className="h-14"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Badge className="bg-red-900/50 text-red-400 border-red-800 rounded-sm">
                <ShieldAlert className="w-3 h-3 mr-1" />
                ADMIN
              </Badge>
              <Button 
                onClick={handleRefresh}
                disabled={refreshing}
                variant="outline"
                className="border-stone-700 text-stone-300"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                onClick={handleLogout}
                variant="ghost"
                className="text-stone-400 hover:text-white"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl text-white uppercase">Payout Dashboard</h1>
            <p className="text-stone-400 mt-1">Platform revenue and transaction overview</p>
          </div>
          <div className="text-right text-sm text-stone-400">
            Logged in as: <span className="text-orange-500">{user?.email}</span>
          </div>
        </div>

        {/* Revenue Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="industrial-card p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-green-500" />
              <Badge className="bg-green-900/50 text-green-400 border-green-800 rounded-sm">MRR</Badge>
            </div>
            <p className="font-heading text-3xl text-orange-500">{formatCurrency(stats?.revenue?.mrr)}</p>
            <p className="text-stone-400 text-sm mt-1">Monthly Recurring Revenue</p>
          </div>

          <div className="industrial-card p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-orange-500" />
            </div>
            <p className="font-heading text-3xl text-orange-500">{formatCurrency(stats?.revenue?.total_subscription_revenue)}</p>
            <p className="text-stone-400 text-sm mt-1">Total Subscription Revenue</p>
          </div>

          <div className="industrial-card p-6">
            <div className="flex items-center justify-between mb-2">
              <CreditCard className="w-8 h-8 text-blue-500" />
            </div>
            <p className="font-heading text-3xl text-white">{formatCurrency(stats?.revenue?.total_job_volume)}</p>
            <p className="text-stone-400 text-sm mt-1">Job Payment Volume</p>
          </div>

          <div className="industrial-card p-6">
            <div className="flex items-center justify-between mb-2">
              <Crown className="w-8 h-8 text-yellow-500" />
            </div>
            <p className="font-heading text-3xl text-orange-500">{stats?.subscribers?.total || 0}</p>
            <p className="text-stone-400 text-sm mt-1">Active Subscribers</p>
          </div>
        </div>

        {/* Subscriber Breakdown & Platform Stats */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Subscribers by Tier */}
          <div className="industrial-card p-6">
            <h2 className="font-heading text-xl text-white uppercase mb-4">Subscribers by Tier</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-stone-900 rounded-sm border border-stone-700">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-stone-500" />
                  <span className="text-white">Basic ($20/mo)</span>
                </div>
                <div className="text-right">
                  <span className="font-heading text-2xl text-orange-500">{stats?.subscribers?.basic || 0}</span>
                  <span className="text-stone-400 text-sm ml-2">= {formatCurrency((stats?.subscribers?.basic || 0) * 20)}/mo</span>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-stone-900 rounded-sm border border-orange-500/50">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <span className="text-white">Professional ($30/mo)</span>
                </div>
                <div className="text-right">
                  <span className="font-heading text-2xl text-orange-500">{stats?.subscribers?.professional || 0}</span>
                  <span className="text-stone-400 text-sm ml-2">= {formatCurrency((stats?.subscribers?.professional || 0) * 30)}/mo</span>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-stone-900 rounded-sm border border-yellow-500/50">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span className="text-white">Premium ($50/mo)</span>
                </div>
                <div className="text-right">
                  <span className="font-heading text-2xl text-orange-500">{stats?.subscribers?.premium || 0}</span>
                  <span className="text-stone-400 text-sm ml-2">= {formatCurrency((stats?.subscribers?.premium || 0) * 50)}/mo</span>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Stats */}
          <div className="industrial-card p-6">
            <h2 className="font-heading text-xl text-white uppercase mb-4">Platform Overview</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-stone-900 rounded-sm border border-stone-700">
                <Users className="w-6 h-6 text-blue-500 mb-2" />
                <p className="font-heading text-2xl text-white">{stats?.users?.customers || 0}</p>
                <p className="text-stone-400 text-sm">Customers</p>
              </div>
              <div className="p-4 bg-stone-900 rounded-sm border border-stone-700">
                <Users className="w-6 h-6 text-orange-500 mb-2" />
                <p className="font-heading text-2xl text-white">{stats?.users?.contractors || 0}</p>
                <p className="text-stone-400 text-sm">Contractors</p>
              </div>
              <div className="p-4 bg-stone-900 rounded-sm border border-stone-700">
                <Briefcase className="w-6 h-6 text-green-500 mb-2" />
                <p className="font-heading text-2xl text-white">{stats?.jobs?.total || 0}</p>
                <p className="text-stone-400 text-sm">Total Jobs</p>
              </div>
              <div className="p-4 bg-stone-900 rounded-sm border border-stone-700">
                <Briefcase className="w-6 h-6 text-emerald-500 mb-2" />
                <p className="font-heading text-2xl text-white">{stats?.jobs?.completed || 0}</p>
                <p className="text-stone-400 text-sm">Completed Jobs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="industrial-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl text-white uppercase">Recent Transactions</h2>
            <Tabs value={txFilter} onValueChange={setTxFilter}>
              <TabsList className="bg-stone-800 border border-stone-700">
                <TabsTrigger value="all" className="text-sm">All</TabsTrigger>
                <TabsTrigger value="subscription" className="text-sm">Subscriptions</TabsTrigger>
                <TabsTrigger value="job_payment" className="text-sm">Job Payments</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-stone-400">
              <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No transactions yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-700">
                    <th className="text-left py-3 px-4 text-stone-400 font-heading uppercase text-sm">Date</th>
                    <th className="text-left py-3 px-4 text-stone-400 font-heading uppercase text-sm">Type</th>
                    <th className="text-left py-3 px-4 text-stone-400 font-heading uppercase text-sm">User</th>
                    <th className="text-left py-3 px-4 text-stone-400 font-heading uppercase text-sm">Details</th>
                    <th className="text-right py-3 px-4 text-stone-400 font-heading uppercase text-sm">Amount</th>
                    <th className="text-right py-3 px-4 text-stone-400 font-heading uppercase text-sm">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-stone-800 hover:bg-stone-900/50">
                      <td className="py-3 px-4 text-stone-300 text-sm">
                        {formatDate(tx.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={`rounded-sm uppercase text-xs ${
                          tx.type === 'subscription' 
                            ? 'bg-purple-900/50 text-purple-400 border-purple-800'
                            : 'bg-blue-900/50 text-blue-400 border-blue-800'
                        }`}>
                          {tx.type === 'subscription' ? 'Subscription' : 'Job Payment'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-white text-sm">{tx.user_name || 'Unknown'}</p>
                        <p className="text-stone-500 text-xs">{tx.user_email || ''}</p>
                      </td>
                      <td className="py-3 px-4 text-stone-400 text-sm">
                        {tx.type === 'subscription' ? (
                          <span className="capitalize">{tx.plan_id} Plan</span>
                        ) : (
                          <span>Job #{tx.job_id?.slice(0, 8)}...</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-heading text-lg ${
                          tx.type === 'subscription' ? 'text-green-500' : 'text-white'
                        }`}>
                          {formatCurrency(tx.amount)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Badge className={`rounded-sm uppercase text-xs ${
                          tx.payment_status === 'paid'
                            ? 'bg-green-900/50 text-green-400 border-green-800'
                            : tx.payment_status === 'pending'
                            ? 'bg-yellow-900/50 text-yellow-400 border-yellow-800'
                            : 'bg-red-900/50 text-red-400 border-red-800'
                        }`}>
                          {tx.payment_status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
